import {
  CheckCircleOutlined,
  CloudUploadOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  SafetyCertificateOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  App,
  Button,
  Card,
  Progress,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  Upload,
} from "antd";
import { useMemo, useState } from "react";

import { ApiError } from "../api/client";
import {
  confirmProjectImport,
  downloadProjectImportErrors,
  downloadProjectImportTemplate,
  validateProjectImport,
  type ImportRowStatus,
  type ProjectImportJob,
  type ProjectImportRow,
} from "../api/projectImports";

const { Dragger } = Upload;

const STATUS_LABELS: Record<ImportRowStatus, string> = {
  VALID: "校验通过",
  INVALID: "校验失败",
  IMPORTED: "已导入",
  FAILED_RECHECK: "确认时失效",
};

const FIELD_LABELS: Record<string, string> = {
  work_name: "作品名称",
  work_type_code: "作品类型代码",
  rights_mode_code: "权利归属方式代码",
  completion_date: "创作完成日期",
  publication_date: "创作发表日期",
  author_public_ids: "作者登记人编号",
  right_holder_public_ids: "著作权人登记人编号",
  agent_name: "代理人名称",
  contact_phone: "联系电话",
  cloud_drive_info: "网盘信息",
  work_summary: "作品简介",
  creation_process: "创作过程",
};

function errorMessage(error: unknown, fallback: string) {
  return error instanceof ApiError ? error.message : fallback;
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function displayRawValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "(空)";
  if (Array.isArray(value)) return value.join("; ");
  if (typeof value === "object") return JSON.stringify(value);
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "bigint" || typeof value === "boolean") {
    return value.toString();
  }
  return "(不支持的值)";
}

export function ProjectImportPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File>();
  const [job, setJob] = useState<ProjectImportJob>();
  const [idempotencyKey, setIdempotencyKey] = useState<string>();

  const validateMutation = useMutation({
    mutationFn: validateProjectImport,
    onSuccess: (result) => {
      setJob(result);
      setIdempotencyKey(crypto.randomUUID());
      message.success(`已完成 ${result.total_rows} 行全量校验，校验期间未创建项目`);
    },
    onError: (error) => message.error(errorMessage(error, "Excel 校验失败")),
  });
  const confirmMutation = useMutation({
    mutationFn: async () => {
      if (!job || !idempotencyKey) throw new Error("导入任务尚未完成校验");
      return confirmProjectImport(job.id, idempotencyKey);
    },
    onSuccess: async (result) => {
      setJob(result);
      message.success(`已导入 ${result.imported_rows} 个项目，失败行未写入`);
      await queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
    onError: (error) => message.error(errorMessage(error, "确认导入失败")),
  });

  const failedRows = useMemo(() => job?.rows.filter((row) => row.errors.length > 0) ?? [], [job]);
  const completionPercent = job
    ? Math.round(((job.valid_rows + job.failed_rows) / Math.max(job.total_rows, 1)) * 100)
    : 0;

  const downloadTemplate = async () => {
    try {
      saveBlob(await downloadProjectImportTemplate(), "项目批量导入模板.xlsx");
    } catch (error) {
      message.error(errorMessage(error, "模板下载失败"));
    }
  };
  const downloadErrors = async () => {
    if (!job) return;
    try {
      saveBlob(await downloadProjectImportErrors(job.id), `导入错误报告-${job.id.slice(0, 8)}.csv`);
    } catch (error) {
      message.error(errorMessage(error, "错误报告下载失败"));
    }
  };

  return (
    <main className="management-page import-page">
      <div className="page-heading">
        <div>
          <Typography.Text className="section-eyebrow">CONTROLLED EXCEL INGESTION</Typography.Text>
          <Typography.Title level={1}>Excel 批量导入</Typography.Title>
          <Typography.Paragraph type="secondary">
            先全文件校验，再由您确认导入。失败行会保留行号、字段、原值和稳定错误码。
          </Typography.Paragraph>
        </div>
        <Button icon={<DownloadOutlined />} onClick={() => void downloadTemplate()}>
          下载最新模板
        </Button>
      </div>

      <section className="import-stage-grid" aria-label="Excel 导入阶段">
        <Card className="import-stage import-stage--active" variant="borderless">
          <span>01</span>
          <div>
            <strong>上传并全量校验</strong>
            <small>校验完成前创建 0 个项目</small>
          </div>
        </Card>
        <Card
          className={job ? "import-stage import-stage--active" : "import-stage"}
          variant="borderless"
        >
          <span>02</span>
          <div>
            <strong>逐行核对结果</strong>
            <small>所有失败原因一次展示</small>
          </div>
        </Card>
        <Card
          className={
            job?.status === "CONFIRMED" ? "import-stage import-stage--done" : "import-stage"
          }
          variant="borderless"
        >
          <span>03</span>
          <div>
            <strong>幂等确认导入</strong>
            <small>重复点击不会重复建项目</small>
          </div>
        </Card>
      </section>

      <Card className="data-card import-upload-card" variant="borderless">
        <Dragger
          accept=".xlsx"
          maxCount={1}
          fileList={file ? [{ uid: file.name, name: file.name, status: "done" }] : []}
          beforeUpload={(nextFile) => {
            setFile(nextFile);
            setJob(undefined);
            return false;
          }}
          onRemove={() => {
            setFile(undefined);
            setJob(undefined);
          }}
          disabled={validateMutation.isPending}
        >
          <p className="ant-upload-drag-icon">
            <CloudUploadOutlined />
          </p>
          <p className="ant-upload-text">拖入已填写的 .xlsx，或点击选择文件</p>
          <p className="ant-upload-hint">单文件不超过 20 MiB，最多 5,000 个非空数据行</p>
        </Dragger>
        <div className="import-upload-actions">
          <Space>
            <SafetyCertificateOutlined />
            <Typography.Text type="secondary">
              不执行公式、宏、外部链接或嵌入对象；作者和著作权人仅接受登记人公共编号。
            </Typography.Text>
          </Space>
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            disabled={!file}
            loading={validateMutation.isPending}
            onClick={() => file && validateMutation.mutate(file)}
          >
            开始全量校验
          </Button>
        </div>
      </Card>

      {job && (
        <>
          <section className="import-metrics" aria-label="校验汇总">
            <Card variant="borderless">
              <Statistic title="数据总行数" value={job.total_rows} />
            </Card>
            <Card variant="borderless">
              <Statistic
                title={job.status === "CONFIRMED" ? "已导入" : "可导入"}
                value={job.status === "CONFIRMED" ? job.imported_rows : job.valid_rows}
                styles={{ content: { color: "#14845b" } }}
              />
            </Card>
            <Card variant="borderless">
              <Statistic
                title="失败行"
                value={job.failed_rows}
                styles={{ content: { color: job.failed_rows ? "#c74343" : undefined } }}
              />
            </Card>
            <Card variant="borderless">
              <Typography.Text type="secondary">校验完成度</Typography.Text>
              <Progress
                percent={completionPercent}
                status={job.failed_rows ? "normal" : "success"}
              />
            </Card>
          </section>

          <Alert
            showIcon
            type={job.status === "CONFIRMED" ? "success" : job.failed_rows ? "warning" : "success"}
            title={
              job.status === "CONFIRMED"
                ? `导入完成：成功 ${job.imported_rows} 行，失败 ${job.failed_rows} 行`
                : `全量校验完成：${job.valid_rows} 行可导入，${job.failed_rows} 行需要修正`
            }
            description={
              job.status === "CONFIRMED"
                ? "同一确认请求再次提交会直接返回本结果，不会产生重复项目。"
                : "点击确认时，服务端还会重新检查登记人状态和当前账号下的同名项目。"
            }
          />

          <Card
            className="data-card import-results-card"
            variant="borderless"
            title="逐行校验结果"
            extra={
              failedRows.length > 0 && (
                <Button icon={<DownloadOutlined />} onClick={() => void downloadErrors()}>
                  下载错误报告
                </Button>
              )
            }
          >
            <Table<ProjectImportRow>
              rowKey="id"
              dataSource={job.rows}
              pagination={{ pageSize: 20, showTotal: (total) => `共 ${total} 行` }}
              scroll={{ x: 980 }}
              expandable={{
                rowExpandable: (record) => record.errors.length > 0,
                expandedRowRender: (record) => (
                  <div className="import-error-list">
                    {record.errors.map((error, index) => (
                      <div
                        className="import-error-item"
                        key={`${error.field}-${error.code}-${index}`}
                      >
                        <WarningOutlined />
                        <div>
                          <strong>{FIELD_LABELS[error.field] ?? error.field}</strong>
                          <span>{error.reason}</span>
                          <small>
                            原值：{displayRawValue(error.raw_value)} · 错误码：{error.code}
                          </small>
                        </div>
                      </div>
                    ))}
                  </div>
                ),
              }}
              columns={[
                { title: "Excel 行", dataIndex: "row_number", width: 100 },
                {
                  title: "作品名称",
                  width: 260,
                  render: (_, record) => displayRawValue(record.raw_values.work_name),
                },
                {
                  title: "状态",
                  dataIndex: "status",
                  width: 130,
                  render: (status: ImportRowStatus) => (
                    <Tag color={status === "VALID" || status === "IMPORTED" ? "success" : "error"}>
                      {STATUS_LABELS[status]}
                    </Tag>
                  ),
                },
                {
                  title: "错误摘要",
                  render: (_, record) =>
                    record.status === "IMPORTED" ? (
                      <Typography.Text type="success">
                        <CheckCircleOutlined /> 导入成功
                      </Typography.Text>
                    ) : record.errors.length ? (
                      <Typography.Text type="danger">
                        {record.errors.map((error) => error.reason).join("；")}
                      </Typography.Text>
                    ) : (
                      <Typography.Text type="success">
                        <CheckCircleOutlined /> 可导入
                      </Typography.Text>
                    ),
                },
                {
                  title: "项目编号",
                  dataIndex: "project_number",
                  width: 180,
                  render: (value: string | null) => value ?? "—",
                },
              ]}
            />
            <div className="import-confirm-bar">
              <div>
                <strong>
                  {job.status === "CONFIRMED" ? "本批次已确认" : `将导入 ${job.valid_rows} 个项目`}
                </strong>
                <small>失败行不会写入数据库，可下载报告修正后重新上传。</small>
              </div>
              <Button
                type="primary"
                size="large"
                icon={<CheckCircleOutlined />}
                disabled={job.status === "CONFIRMED" || job.valid_rows === 0}
                loading={confirmMutation.isPending}
                onClick={() => confirmMutation.mutate()}
              >
                确认导入合法行
              </Button>
            </div>
          </Card>
        </>
      )}
    </main>
  );
}
